import { apiRequest } from '../../../shared/services/apiClient';
import type {
  ReminderEngineBackfillRequest,
  ReminderEngineBackfillResult,
  ReminderEngineChannelInfo,
  ReminderEngineDefinition,
  ReminderEngineDefinitionQuery,
  ReminderEnginePagedResult,
  ReminderEngineScheduledItem,
  ReminderEngineScheduledQuery,
  ProcessScheduledReminderResult,
  ReminderEngineSubjectParameter,
  ReminderEngineSubjectType,
  UpsertReminderDefinitionRequest,
} from '../types/ReminderEngine';

const basePath = '/api/v1/reminder-engine';

const buildQuery = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

export const listReminderChannels = () =>
  apiRequest<ReminderEngineChannelInfo[]>(`${basePath}/channels`);

export const listReminderSubjectTypes = () =>
  apiRequest<ReminderEngineSubjectType[]>(`${basePath}/subject-types`);

export const listReminderSubjectParameters = (subjectType: string) =>
  apiRequest<ReminderEngineSubjectParameter[]>(
    `${basePath}/subject-types/${encodeURIComponent(subjectType)}/parameters`,
  );

export const listReminderDefinitions = (query: ReminderEngineDefinitionQuery = {}) =>
  apiRequest<ReminderEngineDefinition[]>(
    `${basePath}/definitions${buildQuery({
      category: query.category,
      status: query.status,
    })}`,
  );

export const getReminderDefinition = (key: string) =>
  apiRequest<ReminderEngineDefinition>(`${basePath}/definitions/${encodeURIComponent(key)}`);

export const listScheduledReminders = (query: ReminderEngineScheduledQuery = {}) =>
  apiRequest<ReminderEnginePagedResult<ReminderEngineScheduledItem>>(
    `${basePath}/scheduled${buildQuery({
      subjectType: query.subjectType,
      subjectId: query.subjectId,
      definitionKey: query.definitionKey,
      status: query.status,
      page: query.page,
      pageSize: query.pageSize,
    })}`,
  );

export const processScheduledReminder = (scheduledReminderId: string) =>
  apiRequest<ProcessScheduledReminderResult>(`${basePath}/scheduled/${scheduledReminderId}/process`, { method: 'POST' });

export const activateReminderDefinition = (definitionId: string) =>
  apiRequest<ReminderEngineDefinition>(`${basePath}/definitions/${definitionId}/activate`, { method: 'POST' });

export const upsertReminderDefinition = (payload: UpsertReminderDefinitionRequest) =>
  apiRequest<ReminderEngineDefinition>(`${basePath}/definitions`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const backfillReminders = (payload: ReminderEngineBackfillRequest = {}) =>
  apiRequest<ReminderEngineBackfillResult>(`${basePath}/backfill`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
